import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Wordmark } from "@/components/brand/Wordmark";
import { Button } from "@/components/ui/Button";
import { appUrl, env } from "@/lib/env";
import { isNavActive, localizedPath, navItems, preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";

// Full-screen drawer behind the glass-pill navbar. It overlays the pill, so
// it carries its own header row (wordmark + close) instead of relying on the
// navbar staying visible. Rendered through a portal: the pill's
// backdrop-filter makes it the containing block for `fixed` descendants and
// its overflow-hidden would clip the drawer otherwise.

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const items = navItems(env.launched, useDesktopPlatform() !== "unknown");
  const referralCode = new URLSearchParams(location.search).get("ref");

  // biome-ignore lint/correctness/useExhaustiveDependencies: close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div className="md:hidden">
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground"
        aria-expanded={open}
        aria-controls="mobile-nav-panel"
        aria-label={t("nav.openMenu")}
        onClick={() => setOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </button>

      {open
        ? createPortal(
            <div
              id="mobile-nav-panel"
              className="fixed inset-0 z-[60] flex flex-col bg-background/95 backdrop-blur-md"
              role="dialog"
              aria-modal="true"
              aria-label={t("nav.primaryAria")}
            >
              <div className="flex items-center justify-between px-5 py-5">
                <Wordmark tile />
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground"
                  aria-label={t("nav.closeMenu")}
                  onClick={() => setOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-5 pb-8 pt-2">
                {items.map((item) => {
                  const to = preserveSearch(
                    localizedPath(location.pathname, item.path),
                    location.search,
                  );
                  const active = isNavActive(location.pathname, item.path);
                  return (
                    <Link
                      key={item.key}
                      to={to}
                      className={`rounded-full px-4 py-3 text-base ${
                        active ? "bg-muted font-medium text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {t(item.labelKey)}
                    </Link>
                  );
                })}
                <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
                  {env.launched ? (
                    <Button
                      variant="primary"
                      href={appUrl("/sign-in", referralCode)}
                      className="w-full"
                    >
                      {t("nav.signIn")}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      to={preserveSearch("/#waitlist", location.search)}
                      className="w-full"
                    >
                      {t("nav.joinWaitlist")}
                    </Button>
                  )}
                </div>
              </nav>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
